import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { postData } from '../services/api';
import { syncPushNotifications, unregisterPushNotifications } from '../utils/pushNotifications';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [role, setRole] = useState(null);
  const [activeModules, setActiveModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const saveSession = useCallback((data) => {
    const { token, user, tenant, role, activeModules } = data;
    const sessionUser = user && data.studentExperience && !user.studentExperience
      ? { ...user, studentExperience: data.studentExperience }
      : user;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(sessionUser));
    if (tenant) localStorage.setItem('tenantId', tenant.id);
    if (tenant) localStorage.setItem('tenant', JSON.stringify(tenant));
    if (role) localStorage.setItem('role', JSON.stringify(role));
    if (activeModules) localStorage.setItem('activeModules', JSON.stringify(activeModules));
    setUser(sessionUser);
    setTenant(tenant);
    setRole(role);
    setActiveModules(activeModules || []);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenant');
    localStorage.removeItem('role');
    localStorage.removeItem('activeModules');
    localStorage.removeItem('tenant_industry_slug');
    setUser(null);
    setTenant(null);
    setRole(null);
    setActiveModules([]);
  }, []);

  // Rehydrate session on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');
    const storedRole = localStorage.getItem('role');
    const storedModules = localStorage.getItem('activeModules');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        if (storedTenant) setTenant(JSON.parse(storedTenant));
        if (storedRole) setRole(JSON.parse(storedRole));
        if (storedModules) setActiveModules(JSON.parse(storedModules));
        // Refresh modules + role from server in background. The role pull
        // matters: when an admin edits the permission matrix or menuAccess
        // for a role, every affected user's session-cached `role` (in
        // localStorage) is stale. /auth/me populates user.roleId with the
        // live Role doc, so a plain page refresh now picks up the change
        // without the user having to log out and back in.
        // Only for tenant users — SA has no tenant role.
        if (parsedUser?.userType !== 'super_admin') {
          api.get('/auth/me')
            .then(response => {
              const payload = response.data?.data || {};
              const mods = payload.activeModules || [];
              if (payload.user) {
                const freshUser = payload.studentExperience && !payload.user.studentExperience
                  ? { ...payload.user, studentExperience: payload.studentExperience }
                  : payload.user;
                setUser(freshUser);
                localStorage.setItem('user', JSON.stringify(freshUser));
              }
              setActiveModules(mods);
              localStorage.setItem('activeModules', JSON.stringify(mods));
              // user.roleId is the populated Role document (or just an id
              // if population failed). Only refresh when we got the doc.
              const freshRole = payload.user?.roleId;
              if (freshRole && typeof freshRole === 'object' && freshRole._id) {
                setRole(freshRole);
                localStorage.setItem('role', JSON.stringify(freshRole));
              }
              // Re-register push on every page load so subscription stays
              // fresh after browser restarts or SW updates
              syncPushNotifications().catch(() => {});
            })
            .catch(() => {})
            .finally(() => setLoading(false));
          return;
        }
      } catch (e) {
        clearSession();
      }
    }
    setLoading(false);
  }, [clearSession]);

  const login = async (email, password, tenantSlug, tenantId) => {
    const payload = { email, password };
    if (tenantSlug) payload.tenantSlug = tenantSlug; // kept for backward compat
    if (tenantId)   payload.tenantId   = tenantId;
    const res = await postData('/auth/login', payload);
    const data = res.data || res; // unwrap { success, data: {...} }
    // If backend wants tenant selection, return data without saving session
    if (data?.requireTenantSelection) return data;
    saveSession(data);
    // Non-blocking: ask for push permission and register subscription
    syncPushNotifications().catch(() => {});
    return data;
  };

  const loginSuperAdmin = async (email, password) => {
    const res = await postData('/sa/auth/login', { email, password });
    const data = res.data || res; // unwrap { success, data: { token, user } }
    // Clear all stale tenant data from localStorage before setting SA session
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenant');
    localStorage.removeItem('role');
    localStorage.removeItem('activeModules');
    localStorage.removeItem('tenant_industry_slug');
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    setTenant(null);
    setRole(null);
    setActiveModules([]);
    return data;
  };

  const register = async (payload) => {
    const res = await postData('/auth/register', payload);
    const data = res.data || res;
    saveSession(data);
    return data;
  };

  const logout = useCallback(() => {
    // Remove push subscription from server before clearing the token
    unregisterPushNotifications().catch(() => {});
    clearSession();
    window.location.href = '/login';
  }, [clearSession]);

  const refreshModules = async () => {
    try {
      const response = await api.get('/auth/me');
      const payload = response.data?.data || {};
      const mods = payload.activeModules || [];
      if (payload.user) {
        const freshUser = payload.studentExperience && !payload.user.studentExperience
          ? { ...payload.user, studentExperience: payload.studentExperience }
          : payload.user;
        setUser(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
      }
      setActiveModules(mods);
      localStorage.setItem('activeModules', JSON.stringify(mods));
      // Pull the live role too so Roles & Permissions matrix edits propagate
      // to in-app sessions without forcing a logout. See rehydrate effect
      // above for the same reasoning.
      const freshRole = payload.user?.roleId;
      if (freshRole && typeof freshRole === 'object' && freshRole._id) {
        setRole(freshRole);
        localStorage.setItem('role', JSON.stringify(freshRole));
      }
    } catch (e) {}
  };

  // Re-fetch tenant document so components that read `tenant.branding.logo`,
  // `tenant.branding.primaryColor`, `tenant.name`, etc. pick up Settings-page
  // edits without a full page reload.
  const refreshTenant = useCallback(async () => {
    try {
      const response = await api.get('/settings');
      const fresh = response.data?.data || response.data;
      if (fresh) {
        setTenant(fresh);
        localStorage.setItem('tenant', JSON.stringify(fresh));
      }
      return fresh;
    } catch (e) { return null; }
  }, []);

  // In-memory patch (no server round-trip) — useful after an immediate local
  // edit where we already have the updated fields, so TenantLayout's branding
  // effect re-runs with the new values.
  const patchTenant = useCallback((patch) => {
    setTenant(prev => {
      const next = { ...(prev || {}), ...patch };
      if (patch && patch.branding) next.branding = { ...(prev?.branding || {}), ...patch.branding };
      try { localStorage.setItem('tenant', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const hasPermission = useCallback((moduleKey, action) => {
    if (!user) return false;
    if (user.userType === 'super_admin') return true;
    if (!role || !role.permissionMatrix) return false;
    const perms = role.permissionMatrix[moduleKey];
    if (!perms) return false;
    if (role.key === 'admin' || role.key === 'tenant_admin') return true;
    return !!perms[action];
  }, [user, role]);

  const canAccessModule = useCallback((moduleKey) => {
    if (!user) return false;
    if (user.userType === 'super_admin') return true;
    // Core modules always accessible
    const coreModules = ['dashboard', 'users', 'roles', 'settings', 'notifications'];
    if (coreModules.includes(moduleKey)) return true;
    return activeModules.some(m => m.key === moduleKey);
  }, [user, activeModules]);

  const isSuperAdmin = user?.userType === 'super_admin';
  const isAdmin = role?.key === 'admin' || role?.key === 'tenant_admin';
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user, tenant, role, activeModules, loading,
      login, loginSuperAdmin, register, logout,
      saveSession, refreshModules, refreshTenant, patchTenant,
      hasPermission, canAccessModule,
      isSuperAdmin, isAdmin, isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
